import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text
} from '@chakra-ui/react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SFPlusCircleIcon } from '@assets/icons';
import { useInitiativeOptions } from '@services/query-services/initiatives.query-service';
import CustomSelect from '@components/general/form/custom-select.component';
import StepWithLabelComponent from '@components/general/form/step-with-label.component';

const CategorySelectionModal = ({ disclosure, onConfirm, isLoading = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: focusOptions } = useInitiativeOptions({ type: 'Focus' });

  const {
    handleSubmit,
    control,
    reset: resetForm
  } = useForm({
    defaultValues: {
      initiative: null
    }
  });

  const onSubmit = async (formValues) => {
    if (!formValues.initiative) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the onConfirm callback with the selected category
      await onConfirm(formValues.initiative);
      disclosure.onClose();
    } catch (error) {
      console.error('Error in category selection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      {...disclosure}
      variant='classic'
      onCloseComplete={() => {
        resetForm();
        setIsSubmitting(false);
      }}
      data-testid='category-selection-modal'
    >
      <ModalOverlay />
      <ModalContent maxWidth='400px'>
        <ModalHeader>
          <HStack>
            <SFPlusCircleIcon
              boxSize={7}
              p={1}
            />
            <Text variant='body-600-md'>Select Category</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={11}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid='category-form'
          >
            <Stack gap={6}>
              <Controller
                control={control}
                name='initiative'
                rules={{ required: 'Pick a category' }}
                render={({ field: { name, value, onChange }, fieldState }) => (
                  <StepWithLabelComponent
                    name={name}
                    fieldState={fieldState}
                  >
                    <CustomSelect
                      size='md'
                      isMulti={false}
                      placeholder='Category...'
                      options={focusOptions}
                      value={value}
                      onChange={onChange}
                      menuPlacement='auto'
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                      }}
                      data-testid='category-select'
                    />
                  </StepWithLabelComponent>
                )}
              />

              <HStack
                gap={3}
                justifyContent='flex-end'
              >
                <Button
                  variant='reskinSecondary'
                  size='reskinLg'
                  borderRadius='lg'
                  onClick={disclosure.onClose}
                  isDisabled={isSubmitting || isLoading}
                  data-testid='cancel-button'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='reskinPrimary'
                  size='reskinLg'
                  borderRadius='lg'
                  isLoading={isSubmitting || isLoading}
                  data-testid='confirm-category-button'
                >
                  Upload to FORA
                </Button>
              </HStack>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CategorySelectionModal;
